import { create } from "zustand";
import { persist } from "zustand/middleware";
import { deriveAcademicYear, isCanonicalAcademicYear } from "@campushubai/shared";

// ── Academic-year source of truth ───────────────────────────────────────
//
// The Indian school year starts in June. Historically every screen derived
// "the current academic year" inline from `new Date()` with a hard June
// cutoff (`getMonth() >= 5`). That meant a school configured in late May was
// stamped with last year's label and looked unconfigured the moment the
// clock rolled into June — the setup gate, dashboards and every year-scoped
// query silently disagreed about which year was "current".
//
// This store replaces that guessing with an explicit, user-selected year.
// The top-bar <AcademicYearSelector/> writes here; every consumer reads
// `useAcademicYear()`. The derived year is only the initial default.
//
// IMPORTANT: the stored value is the CANONICAL label ("2026-27") that the
// DB uses for SchoolClass.academic, FeePlan.academic, Timetable.academic,
// etc. Display-only formatting ("2026–2027") happens at the edge.

// The canonical label shape + June cutoff live in @campushubai/shared so the
// API validates writes against exactly what the app derives and offers.
// Re-exported here because ~15 call sites already import them from this store.
export { deriveAcademicYear, isCanonicalAcademicYear };

/**
 * 5-year window — previous year through +3 — anchored on the derived
 * current year. Mirrors the setup wizard's window so the selector and the
 * wizard offer the same set. `extra` years (e.g. a persisted selection that
 * falls outside the window, or years the school already has data in) are
 * merged in and the result is sorted + de-duped.
 */
export function academicYearOptions(extra: string[] = [], anchor = deriveAcademicYear()): string[] {
  const startYear = parseInt(anchor.slice(0, 4), 10);
  const set = new Set<string>();
  for (let i = -1; i <= 3; i++) {
    const y = startYear + i;
    set.add(`${y}-${String(y + 1).slice(-2)}`);
  }
  for (const e of extra) if (/^\d{4}-\d{2}$/.test(e)) set.add(e);
  return [...set].sort();
}

/**
 * The half-open date window [start, end) an academic-year label covers.
 * The Indian school year starts in June (mirrors `deriveAcademicYear`), so
 * "2025-26" spans 1 Jun 2025 → 1 Jun 2026 (exclusive). Used to consolidate
 * every exam conducted within a given year regardless of term. Returns null
 * for a malformed label so callers can fall back to "all years".
 */
export function academicYearRange(label: string): { start: Date; end: Date } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(label);
  if (!m) return null;
  const startYear = parseInt(m[1]!, 10);
  // June (month index 5) of the start year → June of the next year, exclusive.
  return { start: new Date(startYear, 5, 1, 0, 0, 0, 0), end: new Date(startYear + 1, 5, 1, 0, 0, 0, 0) };
}

/** True when an ISO/date string falls inside the given academic year's window.
 *  An unparseable label admits everything (so a bad selection never blanks the
 *  card); an unparseable date is excluded. */
export function isDateInAcademicYear(dateStr: string | Date | null | undefined, label: string): boolean {
  const range = academicYearRange(label);
  if (!range) return true;
  if (!dateStr) return false;
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  return d >= range.start && d < range.end;
}

/** "2026-27" → "2026–2027" (en-dash) for display. */
export function formatAcademicYearLong(v: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(v);
  if (!m) return v;
  const start = m[1]!;
  const end = `${start.slice(0, 2)}${m[2]}`;
  return `${start}–${end}`;
}

/**
 * Picks the best year out of the set a school actually holds data in.
 * Prefers the derived current year, then the newest year at or before it
 * (a school mid-rollover keeps working), then the newest overall.
 */
export function bestYearFromData(
  yearsWithData: readonly string[],
  now: Date = new Date(),
): string | null {
  const canonical = [...new Set(yearsWithData.filter(isCanonicalAcademicYear))].sort();
  if (canonical.length === 0) return null;
  const current = deriveAcademicYear(now);
  if (canonical.includes(current)) return current;
  const past = canonical.filter((y) => y < current);
  return past.length > 0 ? past[past.length - 1]! : canonical[canonical.length - 1]!;
}

interface AcademicYearState {
  selectedYear: string;
  /**
   * False while `selectedYear` is only a derived default. Set to true the
   * moment a human picks a year, which is what protects a deliberate
   * "prep next year" selection from being auto-corrected away.
   */
  isExplicit: boolean;
  setSelectedYear: (year: string) => void;
  /**
   * Heal a dead-end selection using the years the active school actually
   * holds data in. No-op when the current selection has data, or when a
   * human pinned a canonical year on purpose. Call this from a data-owning
   * hook — see `useSchoolSetupStatus`.
   */
  reconcileWithData: (yearsWithData: readonly string[]) => void;
  /** Drop back to a derived default (used on logout / tenant change). */
  resetToDerived: () => void;
}

// PERSISTENCE CONTRACT — read before touching `version`.
//
// The stored year used to be written once, at first launch, and never
// re-derived: `persist` rehydrates the saved value over the initial state,
// and there was no `version`/`migrate`. An app first opened before 1 Jun
// 2026 therefore kept "2025-26" forever. Because the year feeds the
// `useSchoolSetupStatus` gate, every year-scoped module rendered as
// "school not set up" — on iOS with no way out, since the native TopBar
// branch omits the year selector. A 685-student school read as empty.
//
// The invariant now: a year the user did NOT choose is a cache, not a
// setting. It is re-derived on every rehydrate, and `reconcileWithData`
// can retarget it once we know which years hold data. Only an explicit
// human choice is sticky.
export const useAcademicYearStore = create<AcademicYearState>()(
  persist(
    (set, get) => ({
      selectedYear: deriveAcademicYear(),
      isExplicit: false,
      setSelectedYear: (year) => set({ selectedYear: year, isExplicit: true }),
      reconcileWithData: (yearsWithData) => {
        const { selectedYear, isExplicit } = get();
        // Nothing to reason about until the probe has landed.
        if (yearsWithData.length === 0) return;
        // The current selection works — leave it alone.
        if (yearsWithData.includes(selectedYear)) return;
        // A deliberate, well-formed choice stays put; the user can see it
        // is empty and switch back themselves. A malformed one cannot be
        // re-selected through any picker, so it is always a dead end.
        if (isExplicit && isCanonicalAcademicYear(selectedYear)) return;
        const best = bestYearFromData(yearsWithData);
        if (best && best !== selectedYear) set({ selectedYear: best });
      },
      resetToDerived: () => set({ selectedYear: deriveAcademicYear(), isExplicit: false }),
    }),
    {
      name: "campushubai-academic-year",
      version: 1,
      // v0 rows carry no `isExplicit`, so we cannot tell a deliberate
      // choice from a first-launch default. Treat them all as implicit:
      // that is what un-sticks the devices already stranded on a stale
      // year, and the cost of being wrong is one re-pick from the picker.
      migrate: (persisted) => ({
        ...(persisted as Partial<AcademicYearState>),
        isExplicit: false,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Re-derive an implicit year against the CURRENT clock, so the
        // June rollover lands without needing a reinstall.
        if (!state.isExplicit || !isCanonicalAcademicYear(state.selectedYear)) {
          state.selectedYear = deriveAcademicYear();
          state.isExplicit = false;
        }
      },
    },
  ),
);

/** Read-only convenience hook for the many call sites that just need the
 *  active academic year. */
export function useAcademicYear(): string {
  return useAcademicYearStore((s) => s.selectedYear);
}

/**
 * Drop the year back to a derived default. For sign-out: the selection is a
 * per-user view preference, so it must not outlive the session on a shared
 * device. Imperative (not a hook) because `auth-store` calls it.
 */
export function resetAcademicYear(): void {
  useAcademicYearStore.getState().resetToDerived();
}

/**
 * Keep the current year but release the human pin, so `reconcileWithData`
 * may retarget it. For tenant switches, where the incoming school may hold
 * data in entirely different years than the one we came from.
 */
export function unpinAcademicYear(): void {
  useAcademicYearStore.setState({ isExplicit: false });
}
